import React, {useContext, useState} from 'react';
import {useParams} from "react-router-dom";


import {makeStyles} from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Button from '@material-ui/core/Button';

import Colors from '../utils/colors';
import apiFetch, {getData} from '../utils/apiUtils';
import {ErrorContext} from "../utils/constants";

const useStyles = makeStyles(() => ({
  h1: {
    marginBottom: '5px'
  },
  container: {
    padding: '1rem',
    width: props => props.containerWidth,
    backgroundColor: Colors.lightBlue,
    marginTop: '1rem',
  },
  inputStyle: {
    backgroundColor: 'white',
  },
  roleStyle: {
    backgroundColor: 'white',
    marginTop: '1rem',
    width: '100%',
  },
  btnStyle: {
    color: 'white',
    backgroundColor: Colors.primary,
    marginTop: '1rem',
    width: '100%',
    height: '40px',
    "&:hover": {
      backgroundColor: Colors.brightBlue,
      color: Colors.indigoBlue
    },
  },
  addCardIcon: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '1.25rem',
  },
  addCardContainer: {
    marginTop: '1rem',
    padding: '0.5rem',
    width: props => props.containerWidth,
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: Colors.lightBlue,
    color: 'black',
    marginBottom: '0.25rem',
    alignItems: 'center'
  },
  pointerCursor: {
    cursor: 'pointer'
  }

}));

export default function AddUserCard({user, teamMembers, disabled, updateForOwner, addTeamMethod, hideRemoveBtn=false}) {
  const classes = useStyles({containerWidth: hideRemoveBtn? '100%' : '32%'});
  const params = useParams();
  const { triggerError } = useContext(ErrorContext);

  const initialUser = user? {displayName: user.name, oid: user.oid} : {};
  const roles = user && user.role === 'Owner' ? ['Owner'] : ['Owner', 'Editor', 'Viewer'];

  const [ initialUserForRemoval, setInitialUserForRemoval ] = useState(user);
  const [ userAutocomplete, setUserAutocomplete ] = useState([]);
  const [ selectedUser, setSelectedUser ] = useState(initialUser);
  const [ currentRole, setCurrentRole ] = useState(user && user.role ? user.role  : 'Editor');
  const [ showCard, setShowCard ] = useState(false);
  const onAutocompleteError = (error) => {
    // deliberately left blank.  We don't want a popup with every letter typed.
    console.log(error);
  };

  const handleAutocompleteChange = (event) => {
    const value = event.target.value;
    if (value.length >= 2) {
      getData('/users/' + value, validateUserAutocomplete, onAutocompleteError);
    } else {
      if (selectedUser) {
        setUserAutocomplete([selectedUser]);
      } else {
        setUserAutocomplete([]);
      }
    }
  };

  const validateUserAutocomplete = (users) => {
    // const teamUsers = selectedUser && selectedUser.displayName?
    //                       teamMembers.filter(tm => tm.name !== selectedUser.displayName ) : teamMembers;

    const initiativeUsers = teamMembers.filter(tm =>  tm.role === 'Owner' || tm.role === 'Editor' || tm.role === 'Viewer')

    const filteredUsers = users.filter(u => initiativeUsers.map(tu => tu.name.toLowerCase())
           .indexOf(u.displayName.toLowerCase()) === -1);
    setUserAutocomplete(filteredUsers);
  };

  const handleUserSelect = (event, selectedValue) => {
    if (!selectedValue) {
      return;
    }
    selectedValue.oid = selectedValue.id;
    setSelectedUser(selectedValue);
    saveUser(selectedValue, currentRole);
  };

  const handleRoleChange = (event) => {
    const value = event.target.value;
    setCurrentRole(value);
    saveUser(selectedUser, value);
  };

  const onError = (selectedValue, currentRole) => () => triggerError({
    title: 'Communication Error',
    description: `We were unable to save team member ${selectedUser} with role ${currentRole}. Please try again.`,
    onRetry: () => saveUser(selectedValue, currentRole),
  });

  const saveUser = (selectedValue, currentRole) => {
    if (selectedValue && selectedValue.oid && currentRole) {
      if (initialUserForRemoval && initialUserForRemoval.initiativeUserId && user) {
        // Update
        user.role = currentRole;
        user.oid = selectedUser;
        const postData = {
          id: user.initiativeUserId,
          role: currentRole,
          oid: selectedValue.oid,
        };

        apiFetch('/initiatives/' + params.id + '/team', 'PUT',
          JSON.stringify(postData),
          (results) => {
            if(currentRole === 'Owner') {
              updateForOwner();
            }
            addTeamMethod && addTeamMethod();
          }, onError(selectedValue, currentRole));
      } else {
        selectedValue.role = currentRole;
        // Create
        apiFetch('/initiatives/' + params.id + '/team', 'POST',
          JSON.stringify(selectedValue),
          (results) => {
            setInitialUserForRemoval({initiativeUserId: results.teamMemberId});
            addTeamMethod && addTeamMethod();
          }, onError(selectedValue, currentRole));
      }
    }
  };

  const onErrorDelete = () => triggerError({
    title: 'Communication Error',
    description: `We were unable to delete team member ${selectedUser} with role ${currentRole}. Please try again.`,
    onRetry: () => handleUserDelete(),
  });

  const handleUserDelete = () => {
    user && apiFetch('/initiatives/team/' + user.initiativeUserId, 'DELETE',
      JSON.stringify({initiative_id: params.id}),
      (results) => {
        // TODO:
        setInitialUserForRemoval(null);
        setSelectedUser({});
        setCurrentRole('Editor');
        setShowCard(false);
        addTeamMethod && addTeamMethod();
      }, onErrorDelete);
  };

  const getIdExtension = () => {
    if (user) {
      return '-' + user.initiativeUserId;
    }
    return '-new';
  };

  const addUserHandler = () =>{
    setShowCard(true);
  };

  const handleOnBlur = () => {
    if (hideRemoveBtn) {
      setShowCard(false);
    }
  };

  const getButton = () => {
    if (hideRemoveBtn) {
      return '';
    }
    return (
      <Button
        className={classes.btnStyle}
        variant="contained"
        color="primary"
        id={'remove-user-btn-' + getIdExtension()}
        disabled={(user && user.role) === 'Owner' || disabled || !initialUserForRemoval || !initialUserForRemoval.initiativeUserId}
        onClick={handleUserDelete}
      >
        REMOVE
      </Button>
    );
  };

  if (!showCard && (!initialUserForRemoval|| !initialUserForRemoval.role)) {
    return (
      <div className={[classes.addCardContainer, classes.pointerCursor].join(' ')} onClick={addUserHandler}>
        <div className={classes.addCardIcon}>
          <AddCircleOutlineIcon />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container} id={'app-user-card-container' + getIdExtension()} onBlur={handleOnBlur}>
      <Autocomplete
        id={'combo-box-person' + getIdExtension()}
        options={userAutocomplete}
        getOptionLabel={option => option? option.displayName : ''}
        style={{ width: '100%' }}
        value={selectedUser}
        size="small"
        onChange={handleUserSelect}
        disabled={disabled}
        renderInput={params => (
          <TextField {...params}
                     variant="outlined"
                     fullWidth
                     className={classes.inputStyle}
                     onChange={handleAutocompleteChange}
                     placeholder="User..."
                     autoFocus={hideRemoveBtn? true : false}
          />
        )}
      />
      <TextField
        id={'text-box-role' + getIdExtension()}
        className={classes.roleStyle}
        select
        variant="outlined"
        size="small"
        label={'ROLE'}
        onChange={handleRoleChange}
        value={currentRole}
        disabled={(user && user.role === 'Owner') || disabled}
        >
          {roles.map((role) => (
            <MenuItem key={role} value={role}>
              {role}
            </MenuItem>
          ))}
      </TextField>

      {getButton()}
    </div>
  );
}
